import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PatientAddressDto } from '../../../api/generated/model';
import { useEndPatientAddressWithDate } from '../../../api/generated/patient-address-controller/patient-address-controller.ts';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { formatDateForInput, formatDateWithGermanMonth, getDateAfterInputValue } from '../../../utils/date.ts';
import { PatientAddressDisplay } from './PatientAddressDisplay.tsx';

interface FormValues {
  until: string;
}

export function EndPatientAddressDialog({
  open,
  onClose,
  patientAddress,
  modalTitle,
  queryKeyToInvalidate,
}: {
  open: boolean;
  onClose: () => void;
  patientAddress: PatientAddressDto;
  modalTitle: string;
  queryKeyToInvalidate: QueryKey;
}) {
  const queryClient = useQueryClient();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: { until: patientAddress.until ?? '' },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const endPatientAddressMutation = useEndPatientAddressWithDate({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate });
        handleOnClose();
      },
      onError: () => toast.error(`Beim beenden der ${modalTitle} ist ein Fehler aufgetreten`),
    },
  });
  const untilMinDate = getDateAfterInputValue(formatDateForInput(patientAddress.from));

  useEffect(() => {
    if (open) {
      reset({
        until: patientAddress.until ?? '',
      });
    }
  }, [open, patientAddress]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 550 }}>
        <ModalClose />
        <Typography level={'h4'}>{modalTitle} beenden</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            endPatientAddressMutation.mutate({
              id: patientAddress.id!,
              data: data.until,
            }),
          )}>
          <Stack gap={2}>
            <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>
                  <Typography>Eintritt</Typography>
                </FormLabel>
                <Typography>{formatDateWithGermanMonth(patientAddress.from)}</Typography>
              </FormControl>
              <FormControl sx={{ flex: 1 }} error={!!errors.until}>
                <FormLabel>
                  <Typography>Austritt *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="until"
                  rules={{
                    required: true,
                    ...(untilMinDate ? { min: { value: untilMinDate, message: 'range' } } : {}),
                  }}
                  min={untilMinDate}
                />
                {errors.until && (
                  <FormHelperText>
                    {errors.until.type === 'required'
                      ? 'End Datum muss angegeben werden'
                      : 'End Datum muss nach dem Eintritt liegen'}
                  </FormHelperText>
                )}
              </FormControl>
            </Stack>
            <FormControl>
              <FormLabel>
                <Typography>Ort</Typography>
              </FormLabel>
              <PatientAddressDisplay patientAddress={patientAddress} />
            </FormControl>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={endPatientAddressMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
